import React from "react"

import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer } from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { useTimeMetricsData } from "@/hooks/useTimeMetricsData"
import { DateFilter } from "@/types/filters"
import { COLORS } from "@/utils/chartColors"

interface TimeMetricsTabProps {
  dateFilter: DateFilter
  jobId?: string
}

const TimeMetricsTab: React.FC<TimeMetricsTabProps> = ({ dateFilter, jobId }) => {
  const { data, isLoading } = useTimeMetricsData(dateFilter, jobId)
  // Convert hours to display format
  const hoursToDisplayFormat = (hours: number): string => {
    if (hours < 24) {
      return `${hours.toFixed(1)} hours`
    } else {
      const days = hours / 24
      return `${days.toFixed(1)} days`
    }
  }

  // Convert hours to days for chart display
  const hoursToDays = (hours: number): number => {
    return hours / 24
  }

  // Get average time to completion from backend data
  const getAverageTimeToCompletion = (): { hours: number; count: number } => {
    if (!data) {
      return { hours: 0, count: 0 }
    }

    return {
      hours: data.avgTimeToCompletion || 0,
      count: data.completedApplicationsCount || 0,
    }
  }

  const totalAverage = getAverageTimeToCompletion()

  // Format data for the chart
  const formatChartData = () => {
    if (!data?.stageDurations) return []

    return data.stageDurations.map((stage) => ({
      name: stage.stageName,
      days: hoursToDays(stage.averageDuration),
      hours: stage.averageDuration,
      count: stage.completionCount,
    }))
  }

  return (
    <div className="space-y-6">
      {/* Average Time to Completion Card */}
      <Card>
        <CardHeader>
          <CardTitle>Average Time to Completion</CardTitle>
          <CardDescription>Average time from application start to completion</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : (
            <div className="flex flex-col items-center justify-center py-4">
              <div className="text-4xl font-bold text-primary">
                {hoursToDisplayFormat(totalAverage.hours)}
              </div>
              <div className="text-sm text-muted-foreground mt-2">
                Based on {totalAverage.count} completed applications
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Average Time per Stage Card */}
      <Card>
        <CardHeader>
          <CardTitle>Average Time Spent in Each Stage</CardTitle>
          <CardDescription>
            Time applicants typically spend in each recruitment stage
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-2">
          <ChartContainer
            className="h-80 w-full"
            config={{
              days: { color: COLORS[4] },
            }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Skeleton className="h-[200px] w-full" />
              </div>
            ) : (
              <BarChart
                height={300}
                data={formatChartData()}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
                style={{ width: "100%" }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  type="number"
                  allowDecimals={true}
                  // domain={[0, "dataMax"]}
                  label={{ value: "Days", angle: -90, position: "insideLeft" }}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    // If the dataKey is 'days', format appropriately
                    if (name === "days") {
                      // Get the original hours value from the data point
                      const dataPoint = formatChartData().find(
                        (item) =>
                          item.name ===
                          (document.querySelector(".recharts-tooltip-item-name")?.textContent ||
                            ""),
                      )

                      const hours = dataPoint ? dataPoint.hours : value * 24

                      // Show hours if less than 1 day, otherwise show days
                      if (hours < 24) {
                        return [`${hours.toFixed(1)} hours`, "Duration"]
                      } else {
                        return [`${value.toFixed(1)} days`, "Duration"]
                      }
                    }
                    return [value, name]
                  }}
                />
                <Bar dataKey="days" fill={COLORS[4]} />
              </BarChart>
            )}
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}

export default TimeMetricsTab
